import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, X, Zap, Clock, Smartphone, Bell, Phone, User, ArrowRight,
  CreditCard, Wallet, BarChart3, Package, Shield, MessageCircle,
  LogIn, ChevronDown, ChevronUp, Stethoscope, Syringe, FileText,
  TrendingUp, Users, Star, Lock, CloudUpload, Plug, CalendarCheck,
  CalendarClock, PawPrint, Download, FlaskConical, Weight, ClipboardList,
  Building2, Send, DoorOpen, Monitor, MapPin
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { createDemoRequest } from '../services/api';
import { toast } from 'sonner';
import { TURKEY_CITIES } from '../constants/cities';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_140853a5-d37e-4724-8f64-c2f7eabb8728/artifacts/4msp2ofk_agrosvetlogo.png';
const NETGSM_LOGO = '/netgsm-logo.svg';
const PAYTR_LOGO = 'https://customer-assets.emergentagent.com/job_8152f177-ab44-4731-90d4-eb859619e536/artifacts/6xdfbmvx_PayTR---2025-New-Logo-Color.png';
const PARASUT_LOGO = 'https://customer-assets.emergentagent.com/job_8152f177-ab44-4731-90d4-eb859619e536/artifacts/v5ti86fd_Parasut-logo%402x.png';
const WHATSAPP_LOGO = 'https://customer-assets.emergentagent.com/job_8152f177-ab44-4731-90d4-eb859619e536/artifacts/vdbscojz_whatsapp-call-icon-psd-editable.png';
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_8152f177-ab44-4731-90d4-eb859619e536/artifacts/rspaa7p7_Ads%C4%B1z%20tasar%C4%B1m%20%2811%29.png';

const Landing = () => {
  const navigate = useNavigate();
  const [demoForm, setDemoForm] = useState({ name: '', phone: '', clinic_name: '', city: '', address: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.landing-reveal, .landing-reveal-left, .landing-reveal-right, .landing-reveal-scale').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!/^0\d{10}$/.test(demoForm.phone)) { toast.error('Telefon numarası 0 ile başlamalı ve 11 haneli olmalıdır'); return; }
    if (!demoForm.clinic_name.trim()) { toast.error('Klinik adını girin'); return; }
    if (!demoForm.city) { toast.error('İl seçin'); return; }
    if (demoForm.password.length < 6) { toast.error('Şifre en az 6 karakter olmalıdır'); return; }
    setSubmitting(true);
    try {
      await createDemoRequest(demoForm);
      setSubmitted(true);
      toast.success('Demo talebiniz alındı!');
    } catch { toast.error('Bir hata oluştu. Lütfen tekrar deneyin.'); }
    finally { setSubmitting(false); }
  };

  const scrollTo = (id) => {
    setMobileMenu(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinks = [
    { label: 'Özellikler', id: 'features' },
    { label: 'Randevu', id: 'appointment-system' },
    { label: 'Entegrasyonlar', id: 'integrations' },
    { label: 'SSS', id: 'faq' },
  ];

  const faqs = [
    { q: 'Kurulum ve geçiş süreci ne kadar sürüyor?', a: 'Hesabınız onaylandıktan sonra dakikalar içinde kullanmaya başlarsınız. Mevcut verilerinizi taşımanız gerekirse destek ekibimiz yardımcı olur.' },
    { q: 'Telefonumdan kullanabilir miyim?', a: 'Evet. AGROSVET tam mobil uyumlu bir web uygulamasıdır. Telefonunuza PWA olarak yükleyebilir, uygulama gibi kullanabilirsiniz.' },
    { q: 'Verilerim güvende mi?', a: 'Tüm verileriniz şifreli olarak bulut sunucularda saklanır. Günlük yedekleme yapılır. Verilerinize yalnızca sizin yetkilendirdiğiniz kişiler erişebilir.' },
    { q: 'Birden fazla kliniği yönetebilir miyim?', a: 'Evet. Çoklu klinik desteği ile tek hesaptan birden fazla kliniği yönetebilirsiniz. Personel klinikler arası geçiş yapabilir.' },
    { q: 'SMS hatırlatma nasıl çalışıyor?', a: 'Aşı ve tedavi tarihleri sisteme girildiğinde, müşterilerinize belirlediğiniz süre öncesinde otomatik SMS gönderilir.' },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ─────── NAVBAR ─────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src={LOGO_URL} alt="AGROSVET" className="w-9 h-9 object-contain" />
            <span className="font-extrabold text-gray-900 text-lg tracking-tight">AGROSVET</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors">{l.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-sm font-medium text-gray-700 hover:text-gray-900 hidden sm:flex" data-testid="nav-login-btn">
              <LogIn size={16} className="mr-1.5" /> Giriş Yap
            </Button>
            <Button onClick={() => scrollTo('demo-form')} className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-full text-sm px-5 h-9 font-semibold" data-testid="nav-cta-btn">
              Ücretsiz Dene
            </Button>
            <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2 rounded-lg hover:bg-gray-100">
              <div className="space-y-1.5">
                <span className="block w-5 h-0.5 bg-gray-700"></span>
                <span className="block w-5 h-0.5 bg-gray-700"></span>
                <span className="block w-3 h-0.5 bg-gray-700"></span>
              </div>
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-3 space-y-1">
            {navLinks.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left py-2.5 text-sm text-gray-700 font-medium">{l.label}</button>
            ))}
            <button onClick={() => { setMobileMenu(false); navigate('/login'); }} className="block w-full text-left py-2.5 text-sm text-[#6b6de3] font-semibold">Giriş Yap</button>
          </div>
        )}
      </nav>

      {/* ─────── HERO ─────── */}
      <section className="pt-28 pb-16 sm:pt-36 sm:pb-24 px-4 relative overflow-hidden">
        {/* Pet Background Image */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/d9d47db6-225d-4310-9f65-c130f777265f/images/f71f6efabe2fba93040ffc36846fe46e00118d6b5168d2fbd65e8377ff291799.png"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white/90" />
        </div>

        {/* Animated Mesh Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="mesh-blob w-[500px] h-[500px] bg-[#6b6de3] -top-48 -left-40" style={{animationDelay:'0s'}} />
          <div className="mesh-blob w-[400px] h-[400px] bg-[#a78bfa] top-20 -right-32" style={{animationDelay:'4s'}} />
          <div className="mesh-blob w-[300px] h-[300px] bg-[#818cf8] bottom-0 left-1/3" style={{animationDelay:'8s'}} />
        </div>

        {/* Floating Shapes */}
        <div className="absolute top-32 left-8 w-16 h-16 bg-[#6b6de3]/10 rounded-2xl float-slow hidden lg:block" />
        <div className="absolute top-48 right-12 w-10 h-10 bg-amber-300/20 rounded-full float-slower hidden lg:block" />
        <div className="absolute bottom-32 left-16 w-8 h-8 bg-emerald-400/15 rounded-xl float-slower hidden lg:block" style={{animationDelay:'2s'}} />
        <div className="absolute bottom-40 right-20 w-14 h-14 bg-[#6b6de3]/8 rounded-full float-slow hidden lg:block" style={{animationDelay:'3s'}} />

        <div className="max-w-5xl mx-auto text-center relative">
          <div className="landing-reveal inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-[#6b6de3] px-5 py-2 rounded-full text-sm font-semibold mb-8 shadow-sm border border-[#6b6de3]/10">
            <Zap size={15} className="text-amber-500" /> Veteriner klinikleri için tasarlandı
          </div>
          <h1 className="landing-reveal stagger-1 text-4xl sm:text-5xl lg:text-7xl font-extrabold text-gray-900 leading-[1.08] mb-6 tracking-tight">
            Kliniğinizi yönetmenin{' '}<br className="hidden sm:block" />
            <span className="inline-flex items-baseline gap-[0.2em]">
              <span className="hero-rotate-wrapper text-[#6b6de3]">
                <span className="invisible" aria-hidden="true">en akıllı</span>
                <span className="hero-rotate-word">en kolay</span>
                <span className="hero-rotate-word">en hızlı</span>
                <span className="hero-rotate-word">en akıllı</span>
              </span>
              <span>yolu</span>
            </span>
          </h1>
          <p className="landing-reveal stagger-2 text-base sm:text-lg lg:text-xl text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed">
            Hasta takip, stok, ödeme, bakiye, e-fatura ve otomatik SMS hatırlatma. Tüm klinik ihtiyaçlarınız tek bir platformda.
          </p>
          <div className="landing-reveal stagger-3 flex flex-col sm:flex-row gap-3 justify-center mb-16">
            <Button onClick={() => scrollTo('demo-form')} className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-full text-base px-8 h-14 py-3.5 font-semibold shadow-xl shadow-[#6b6de3]/30 hover:shadow-2xl hover:shadow-[#6b6de3]/40 transition-all duration-300 hover:scale-[1.02]" data-testid="hero-cta-btn">
              15 Gün Ücretsiz Dene <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button variant="outline" onClick={() => navigate('/login')} className="rounded-full text-base px-8 h-14 py-3.5 border-gray-200 text-gray-700 hover:bg-white hover:border-gray-300 font-semibold backdrop-blur-sm bg-white/60" data-testid="hero-login-btn">
              Giriş Yap
            </Button>
          </div>

          {/* Hero Visual */}
          <div className="landing-reveal-scale stagger-4 relative max-w-lg mx-auto">
            <div className="absolute -inset-6 bg-gradient-to-r from-[#6b6de3]/25 via-[#a78bfa]/20 to-[#6b6de3]/25 rounded-[2.5rem] blur-3xl" />
            <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-gray-200/80 border border-white/80 p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-gradient-to-br from-[#6b6de3] to-[#a78bfa] rounded-2xl flex items-center justify-center shadow-lg shadow-[#6b6de3]/20">
                  <Stethoscope size={24} className="text-white" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900">Max</p>
                  <p className="text-xs text-gray-400">Golden Retriever &middot; Ahmet Y.</p>
                </div>
                <span className="ml-auto text-xs font-bold bg-red-50 text-red-500 px-3 py-1.5 rounded-xl">-350 TL</span>
              </div>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between p-3.5 bg-gradient-to-r from-green-50 to-emerald-50/50 rounded-2xl">
                  <div className="flex items-center gap-2.5"><div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center"><Syringe size={16} className="text-green-600" /></div><span className="text-sm font-medium text-green-700">Kuduz Aşısı</span></div>
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center"><Check size={14} className="text-white" /></div>
                </div>
                <div className="flex items-center justify-between p-3.5 bg-[#F2F2F7] rounded-2xl">
                  <span className="text-sm text-gray-500">Nakit: 200 TL</span>
                  <span className="text-xs text-orange-500 font-semibold bg-orange-50 px-2.5 py-1 rounded-lg">Veresiye: 150 TL</span>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-8 sm:gap-14 mt-16">
            {[
              { value: '100+', label: 'Aktif Klinik' },
              { value: '4.9/5', label: 'Kullanıcı Puanı' },
              { value: '%99.9', label: 'Erişebilirlik' },
            ].map((b, i) => (
              <div key={i} className={`landing-reveal stagger-${i + 5} text-center group`}>
                <p className="text-3xl sm:text-4xl font-extrabold text-gray-900 group-hover:text-[#6b6de3] transition-colors">{b.value}</p>
                <p className="text-xs sm:text-sm text-gray-400 font-medium mt-1">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── PROBLEM → SOLUTION ─────── */}
      <section className="py-20 px-4 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <h2 className="landing-reveal text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-4 tracking-tight">Eski yöntemler vs. AGROSVET</h2>
          <p className="landing-reveal stagger-1 text-gray-500 text-center mb-12 max-w-lg mx-auto">Klinik yönetiminde yeni dönem</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="landing-reveal-left stagger-2 bg-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center"><X size={16} className="text-red-500" /></div>
                <h3 className="font-bold text-gray-900">Eski Yöntem</h3>
              </div>
              <ul className="space-y-3">
                {['Deftere yazılan veresiye, kayıp tahsilat', 'Aşı hatırlatması için tek tek arama', 'Excel tablolarında kayıp hasta bilgileri', 'Stok sayımı için iş bırakan ekip', 'Gün sonu kasa kapama stresi'].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-500">
                    <X size={15} className="text-red-400 flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="landing-reveal-right stagger-3 bg-[#6b6de3] rounded-2xl p-6 text-white">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><Check size={16} className="text-white" /></div>
                <h3 className="font-bold">AGROSVET ile</h3>
              </div>
              <ul className="space-y-3">
                {['Otomatik bakiye takibi, tahsilat SMS ile hatırlatma', 'SMS ve WhatsApp ile otomatik aşı hatırlatma', 'Anlık aranabilir hasta ve müşteri veritabanı', 'Barkodlu stok takibi, SKT uyarısı', 'Otomatik kasa raporu, nakit-kart dağılımı'].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                    <Check size={15} className="text-white flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── FEATURES ─────── */}
      <section id="features" className="py-24 px-4 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#6b6de3]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 left-0 w-80 h-80 bg-amber-200/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="landing-reveal inline-flex items-center gap-2 bg-[#6b6de3]/10 text-[#6b6de3] px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Zap size={15} /> Tüm Özellikler
            </div>
            <h2 className="landing-reveal stagger-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight">Her ihtiyacınız tek platformda</h2>
            <p className="landing-reveal stagger-2 text-gray-500 mt-4 max-w-lg mx-auto text-base">Klinik yönetimi için ihtiyacınız olan her şey, fazlası değil</p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Hero Card - 5 Saniyede İşlem */}
            <div className="landing-reveal-scale stagger-1 col-span-2 row-span-2 group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#6b6de3] to-[#8b5cf6] p-8 text-white cursor-default">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              <div className="relative">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <Zap size={28} className="text-white" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-extrabold mb-3">5 Saniyede İşlem</h3>
                <p className="text-white/80 leading-relaxed text-base max-w-sm">Müşteriyi arayın, hastayı seçin, aşı veya tedaviyi ekleyin. Kasa işlemi anında tamamlansın.</p>
                <div className="flex gap-3 mt-6">
                  <span className="text-xs bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">Hızlı Kasa</span>
                  <span className="text-xs bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">Tek Ekran</span>
                  <span className="text-xs bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">Otomatik</span>
                </div>
              </div>
            </div>

            {/* Hasta Dosyası */}
            <div className="landing-reveal-scale stagger-2 col-span-1 group rounded-3xl bg-gradient-to-br from-teal-50 to-emerald-50 p-6 border border-teal-100/50 hover:shadow-xl hover:shadow-teal-100/30 transition-all duration-300 cursor-default">
              <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-emerald-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-teal-200/50">
                <ClipboardList size={20} className="text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Hasta Dosyası</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Notlar, lab sonuçları, dosyalar tek yerde.</p>
            </div>

            {/* Laboratuvar */}
            <div className="landing-reveal-scale stagger-3 col-span-1 group rounded-3xl bg-gradient-to-br from-cyan-50 to-sky-50 p-6 border border-cyan-100/50 hover:shadow-xl hover:shadow-cyan-100/30 transition-all duration-300 cursor-default">
              <div className="w-11 h-11 bg-gradient-to-br from-cyan-500 to-sky-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-200/50">
                <FlaskConical size={20} className="text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Laboratuvar Takibi</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Kan, idrar, biyokimya. PDF veya fotoğraf.</p>
            </div>

            {/* Kilo Geçmişi */}
            <div className="landing-reveal-scale stagger-4 col-span-1 group rounded-3xl bg-gradient-to-br from-amber-50 to-yellow-50 p-6 border border-amber-100/50 hover:shadow-xl hover:shadow-amber-100/30 transition-all duration-300 cursor-default">
              <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-yellow-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-amber-200/50">
                <Weight size={20} className="text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Kilo Geçmişi</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Trend otomatik hesaplansın, sahibi görsün.</p>
            </div>

            {/* Konaklama */}
            <div className="landing-reveal-scale stagger-5 col-span-1 group rounded-3xl bg-gradient-to-br from-violet-50 to-purple-50 p-6 border border-violet-100/50 hover:shadow-xl hover:shadow-violet-100/30 transition-all duration-300 cursor-default">
              <div className="w-11 h-11 bg-gradient-to-br from-violet-500 to-purple-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-violet-200/50">
                <DoorOpen size={20} className="text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Konaklama</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Oda/kafes, giriş-çıkış, günlük ücret.</p>
            </div>

            {/* SMS & WhatsApp - wide card */}
            <div className="landing-reveal-scale stagger-6 col-span-2 group relative overflow-hidden rounded-3xl bg-gradient-to-r from-sky-500 to-cyan-400 p-6 text-white cursor-default">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-10">
                <MessageCircle size={120} />
              </div>
              <div className="relative flex items-center gap-5">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={26} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-1">SMS & WhatsApp</h3>
                  <p className="text-white/80 text-sm">Aşı hatırlatmaları otomatik gönderilsin. Borç bildirimi tek tuşla ulaşsın.</p>
                </div>
              </div>
            </div>

            {/* Bakiye & Veresiye - wide card */}
            <div className="landing-reveal-scale stagger-7 col-span-2 group relative overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 to-amber-400 p-6 text-white cursor-default">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-10">
                <Wallet size={120} />
              </div>
              <div className="relative flex items-center gap-5">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Wallet size={26} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-1">Bakiye & Veresiye</h3>
                  <p className="text-white/80 text-sm">Tahsilat ve borç takibi. Veresiye otomatik bakiyeye yansısın.</p>
                </div>
              </div>
            </div>

            {/* Bottom row - 4 compact cards */}
            <div className="landing-reveal-scale stagger-8 col-span-1 group rounded-3xl bg-[#FAFAFA] p-5 border border-gray-100 hover:shadow-xl hover:bg-white transition-all duration-300 cursor-default">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-400 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                <CreditCard size={18} className="text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Esnek Ödeme</h3>
              <p className="text-[11px] text-gray-500 leading-relaxed">Nakit, kart, parçalı ödeme.</p>
            </div>

            <div className="landing-reveal-scale stagger-9 col-span-1 group rounded-3xl bg-[#FAFAFA] p-5 border border-gray-100 hover:shadow-xl hover:bg-white transition-all duration-300 cursor-default">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-violet-400 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                <BarChart3 size={18} className="text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Detaylı Raporlar</h3>
              <p className="text-[11px] text-gray-500 leading-relaxed">Ciro, stok, personel performansı.</p>
            </div>

            <div className="landing-reveal-scale stagger-10 col-span-1 group rounded-3xl bg-[#FAFAFA] p-5 border border-gray-100 hover:shadow-xl hover:bg-white transition-all duration-300 cursor-default">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-400 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                <Package size={18} className="text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Stok & Barkod</h3>
              <p className="text-[11px] text-gray-500 leading-relaxed">SKT uyarısı, minimum stok bildirimi.</p>
            </div>

            <div className="landing-reveal-scale stagger-11 col-span-1 group rounded-3xl bg-[#FAFAFA] p-5 border border-gray-100 hover:shadow-xl hover:bg-white transition-all duration-300 cursor-default">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-blue-400 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                <Building2 size={18} className="text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Müşteri Paneli</h3>
              <p className="text-[11px] text-gray-500 leading-relaxed">Hayvan bilgileri, randevular.</p>
            </div>

            {/* Full width - Her Cihazda */}
            <div className="landing-reveal-scale stagger-12 col-span-2 lg:col-span-4 group rounded-3xl bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-6 sm:p-8 text-white relative overflow-hidden cursor-default">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2230%22%20height%3D%2230%22%20viewBox%3D%220%200%2030%2030%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2215%22%20cy%3D%2215%22%20r%3D%221%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E')]" />
              <div className="relative flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
                    <Smartphone size={24} className="text-white" />
                  </div>
                  <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
                    <Monitor size={24} className="text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">Her Cihazda Çalışır</h3>
                  <p className="text-white/60 text-sm">Telefon, tablet veya masaüstü. İnterneti olan her yerden kliniğinize erişin.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── APPOINTMENT SYSTEM ─────── */}
      <section id="appointment-system" className="py-24 px-4 bg-[#FAFAFA] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-80 h-80 bg-[#6b6de3]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-100/20 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <div className="landing-reveal-left">
              <div className="inline-flex items-center gap-2 bg-[#6b6de3]/10 text-[#6b6de3] px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <CalendarClock size={15} /> Online Randevu Sistemi
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-5 tracking-tight leading-tight">
                Randevu yönetimi<br />artık <span className="text-[#6b6de3]">dijital</span>
              </h2>
              <p className="text-gray-500 mb-10 leading-relaxed text-base max-w-md">
                Müşterileriniz kolayca online randevu alsın, siz tek tıkla yönetin. Çakışma yok, karışıklık yok.
              </p>

              <div className="space-y-5">
                {[
                  { icon: PawPrint, title: 'Kolay Randevu Alma', desc: 'Pet ve işlem seçerek müsait saatleri görür, anında talep eder.', gradient: 'from-[#6b6de3] to-[#8b5cf6]' },
                  { icon: CalendarCheck, title: 'Akıllı Takvim', desc: 'Çalışma saati ve kapasiteye göre slotlar otomatik hesaplanır.', gradient: 'from-emerald-500 to-teal-400' },
                  { icon: Stethoscope, title: 'Klinik Kontrolü', desc: 'Gelen talepleri onaylayın, reddedin veya doğrudan oluşturun.', gradient: 'from-rose-500 to-pink-400' },
                ].map((f, i) => (
                  <div key={i} className={`group flex items-start gap-4 p-4 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300 cursor-default`}>
                    <div className={`w-12 h-12 bg-gradient-to-br ${f.gradient} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <f.icon size={22} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{f.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Visual Mockup */}
            <div className="landing-reveal-right">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-[#6b6de3]/20 via-purple-100/30 to-emerald-50/20 rounded-[2.5rem] blur-3xl" />
                <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100/80 p-6 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#6b6de3] to-[#8b5cf6] rounded-xl flex items-center justify-center">
                        <CalendarClock size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">Randevu Takvimi</p>
                        <p className="text-[10px] text-gray-400">Bugün &middot; 6 randevu</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">3 boş slot</span>
                  </div>

                  {[
                    { time: '09:00', pet: 'Max', owner: 'Ahmet Y.', type: 'Kuduz Aşısı', status: 'completed', statusColor: 'bg-green-100 text-green-700', borderColor: 'border-l-green-500' },
                    { time: '10:30', pet: 'Luna', owner: 'Ayşe K.', type: 'Karma Aşı', status: 'onaylandı', statusColor: 'bg-blue-100 text-blue-700', borderColor: 'border-l-blue-500' },
                    { time: '11:00', pet: 'Pamuk', owner: 'Fatma D.', type: 'Muayene', status: 'bekliyor', statusColor: 'bg-amber-100 text-amber-700', borderColor: 'border-l-amber-500' },
                    { time: '14:00', pet: 'Boncuk', owner: 'Mehmet T.', type: 'Diş Temizliği', status: 'yeni talep', statusColor: 'bg-purple-100 text-purple-700', borderColor: 'border-l-purple-500' },
                  ].map((slot, i) => (
                    <div key={i} className={`flex items-center gap-4 p-3.5 rounded-2xl bg-[#FAFAFA] border border-gray-100 border-l-[3px] ${slot.borderColor} hover:shadow-md transition-shadow`}>
                      <div className="text-center min-w-[3rem]">
                        <p className="text-xs font-bold text-gray-900">{slot.time}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 text-sm truncate">{slot.pet} <span className="font-normal text-gray-400 text-xs">({slot.owner})</span></p>
                        <p className="text-[11px] text-gray-500">{slot.type}</p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${slot.statusColor}`}>{slot.status}</span>
                    </div>
                  ))}

                  <div className="flex items-center gap-3 pt-2">
                    {[
                      { icon: Clock, label: 'Slot Bazlı', color: 'bg-[#6b6de3]/10 text-[#6b6de3]' },
                      { icon: Shield, label: 'Çakışma Koruması', color: 'bg-emerald-50 text-emerald-600' },
                      { icon: Bell, label: 'Anlık Bildirim', color: 'bg-amber-50 text-amber-600' },
                    ].map((tag, i) => (
                      <span key={i} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1.5 rounded-full ${tag.color}`}>
                        <tag.icon size={11} /> {tag.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── VACCINE CALENDAR ─────── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="landing-reveal-left">
              <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Syringe size={15} /> Aşı Takvimi
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
                Hiçbir aşı tarihi kaçmasın
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                Hastalarınızın tüm aşı geçmişini takip edin. Yaklaşan aşılar otomatik hatırlatılsın.
                Haftalık ve aylık takvim görünümüyle tüm randevularınızı tek ekranda görün.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Akıllı Durum Sistemi', desc: 'Yapılmadı, Arandı, Yapıldı. Her durum değişikliği kayıt altında.' },
                  { title: 'Haftalık & Aylık Görünüm', desc: 'Her hasta her gün tek bakışta. Renk kodlu kartlarla hızlı tarama.' },
                  { title: 'Otomatik SMS Hatırlatma', desc: 'Aşı tarihi yaklaştığında müşteriye otomatik SMS gönderilir.' },
                  { title: 'Geçmiş & Denetim İzi', desc: 'Tüm durum değişiklikleri detaylı loglanır. Kim, ne zaman, ne yaptı.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={14} className="text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{f.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="landing-reveal-right">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-red-100/60 via-orange-50/40 to-transparent rounded-[2rem] blur-2xl" />
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-5 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900 text-sm">Mart 2026</span>
                    <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">12 yaklaşan aşı</span>
                  </div>
                  {[
                    { pet: 'Max', owner: 'Ahmet Y.', vaccine: 'Kuduz Aşısı', status: 'completed', color: 'border-l-green-500 bg-green-50' },
                    { pet: 'Luna', owner: 'Ayşe K.', vaccine: 'Karma Aşı', status: 'informed', color: 'border-l-blue-500 bg-blue-50' },
                    { pet: 'Boncuk', owner: 'Mehmet D.', vaccine: 'İç Parazit', status: 'pending', color: 'border-l-red-400 bg-red-50/50' },
                  ].map((item, i) => (
                    <div key={i} className={`rounded-xl border border-gray-100 border-l-[3px] ${item.color} px-4 py-3`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-gray-900 text-sm">{item.pet} <span className="font-normal text-gray-400 text-xs">({item.owner})</span></p>
                          <p className="text-xs text-gray-500 mt-0.5">{item.vaccine}</p>
                        </div>
                        <span className={`text-[10px] font-medium px-2 py-1 rounded-full ${
                          item.status === 'completed' ? 'bg-green-100 text-green-700' :
                          item.status === 'informed' ? 'bg-blue-100 text-blue-700' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {item.status === 'completed' ? 'Yapıldı' : item.status === 'informed' ? 'Arandı' : 'Bekliyor'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── PATIENT FILE ─────── */}
      <section className="py-20 px-4 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="landing-reveal-right order-2 md:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-teal-100/60 via-emerald-50/40 to-transparent rounded-[2rem] blur-2xl" />
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-5 space-y-3">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                      <PawPrint size={20} className="text-teal-600" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">Luna</p>
                      <p className="text-[10px] text-gray-400">Golden Retriever · Dişi · 3 yaş</p>
                    </div>
                    <span className="ml-auto text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">3.2 kg</span>
                  </div>
                  {[
                    { label: 'Muayene Notu', desc: 'Genel durumu iyi. Sol arka bacakta hafif topallama...', time: 'Bugün', color: 'border-l-teal-500 bg-teal-50/50' },
                    { label: 'Biyokimya Sonucu', desc: 'ALT: 45, AST: 38, BUN: 22 - Normal sınırlarda', time: '2 gün önce', color: 'border-l-cyan-500 bg-cyan-50/50' },
                    { label: 'Kilo Kaydı', desc: '3.2 kg (+200g)', time: '1 hafta önce', color: 'border-l-amber-500 bg-amber-50/50' },
                  ].map((item, i) => (
                    <div key={i} className={`rounded-xl border border-gray-100 border-l-[3px] ${item.color} px-4 py-3`}>
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-gray-900 text-xs">{item.label}</p>
                        <span className="text-[10px] text-gray-400">{item.time}</span>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="landing-reveal-left order-1 md:order-2">
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-600 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <ClipboardList size={15} /> Dijital Hasta Dosyası
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
                Her hastanın geçmişi parmaklarınızın ucunda
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                Muayene notları, laboratuvar sonuçları, kilo takibi ve dosya ekleri tek bir hasta dosyasında.
                Geçmişe dönüp bakmak artık klasörler arasında kaybolmak değil.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Muayene Notları', desc: 'Her ziyarette not ekleyin. Fotoğraf veya PDF dosyası iliştirin. Tüm geçmiş kronolojik sırada.' },
                  { title: 'Laboratuvar Sonuçları', desc: 'Biyokimya, hemogram, idrar... Sonuç tipine göre filtreleyin, karşılaştırın.' },
                  { title: 'Kilo Takibi & Trend', desc: 'Her muayenede kilo kaydedin. Artış-azalış trendi otomatik hesaplansın.' },
                  { title: 'Müşteri de Görsün', desc: 'Hayvan sahibi kendi panelinden kilo geçmişini ve bilgileri takip edebilsin.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={14} className="text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{f.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── BOARDING ─────── */}
      <section className="py-24 px-4 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-0 w-80 h-80 bg-violet-100/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-50/30 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Visual Mockup */}
            <div className="landing-reveal-left">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-violet-100/50 via-purple-50/30 to-orange-50/20 rounded-[2.5rem] blur-3xl" />
                <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100/80 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-400 rounded-xl flex items-center justify-center">
                        <DoorOpen size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">Konaklama Durumu</p>
                        <p className="text-[10px] text-gray-400">Bugün &middot; 8 oda</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full">%75 doluluk</span>
                  </div>

                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {[
                      { id: 'A1', status: 'occupied', pet: 'Max' },
                      { id: 'A2', status: 'occupied', pet: 'Luna' },
                      { id: 'A3', status: 'available', pet: '' },
                      { id: 'A4', status: 'occupied', pet: 'Boncuk' },
                      { id: 'B1', status: 'occupied', pet: 'Pamuk' },
                      { id: 'B2', status: 'available', pet: '' },
                      { id: 'B3', status: 'occupied', pet: 'Karamel' },
                      { id: 'B4', status: 'occupied', pet: 'Çakıl' },
                    ].map((room, i) => (
                      <div key={i} className={`rounded-xl p-2.5 text-center border transition-all ${
                        room.status === 'occupied'
                          ? 'bg-violet-50 border-violet-200'
                          : 'bg-emerald-50 border-emerald-200 border-dashed'
                      }`}>
                        <p className="text-[10px] font-bold text-gray-500">{room.id}</p>
                        {room.pet ? (
                          <p className="text-[10px] font-semibold text-violet-600 mt-0.5">{room.pet}</p>
                        ) : (
                          <p className="text-[10px] text-emerald-500 mt-0.5">Boş</p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {[
                      { pet: 'Max', owner: 'Ahmet Y.', room: 'A1', days: '3 gün', price: '450 TL', color: 'border-l-violet-500' },
                      { pet: 'Luna', owner: 'Ayşe K.', room: 'A2', days: '1 gün', price: '150 TL', color: 'border-l-orange-500' },
                    ].map((item, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-xl bg-[#FAFAFA] border border-gray-100 border-l-[3px] ${item.color}`}>
                        <div>
                          <p className="font-bold text-gray-900 text-xs">{item.pet} <span className="font-normal text-gray-400">({item.owner})</span></p>
                          <p className="text-[10px] text-gray-500">Oda {item.room} &middot; {item.days}</p>
                        </div>
                        <span className="text-xs font-bold text-gray-900">{item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="landing-reveal-right">
              <div className="inline-flex items-center gap-2 bg-violet-50 text-violet-600 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <DoorOpen size={15} /> Konaklama Yönetimi
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-5 tracking-tight leading-tight">
                Pet otel yönetimi<br />hiç bu kadar <span className="text-violet-600">kolay</span> olmamıştı
              </h2>
              <p className="text-gray-500 mb-10 leading-relaxed text-base max-w-md">
                Oda ve kafes planınızı oluşturun. Giriş-çıkış saatleri, ücretlendirme ve doluluk takibi tek ekranda.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Building2, title: 'Oda & Kafes Planı', desc: 'Hangisi dolu, hangisi boş anlık görün.', gradient: 'from-violet-500 to-purple-400' },
                  { icon: Clock, title: 'Saatlik Ücret', desc: 'Giriş-çıkış saatine göre otomatik hesaplama.', gradient: 'from-orange-500 to-amber-400' },
                  { icon: Users, title: 'Personel Takibi', desc: 'Kim teslim aldı, kim teslim etti?', gradient: 'from-blue-500 to-cyan-400' },
                  { icon: BarChart3, title: 'Doluluk Raporu', desc: 'Aylık gelir ve kalış süresi analizi.', gradient: 'from-emerald-500 to-teal-400' },
                ].map((f, i) => (
                  <div key={i} className={`landing-reveal-scale stagger-${i + 3} group bg-[#FAFAFA] rounded-2xl p-5 border border-gray-100 hover:bg-white hover:shadow-lg transition-all duration-300 cursor-default`}>
                    <div className={`w-10 h-10 bg-gradient-to-br ${f.gradient} rounded-xl flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <f.icon size={18} className="text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── SMS & WHATSAPP ─────── */}
      <section className="py-20 px-4 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="landing-reveal-left">
              <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-600 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Send size={15} /> İletişim & Bildirimler
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
                Müşterilerinize her zaman ulaşın
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                Aşı zamanı geldiğinde, randevu yaklaştığında veya borç hatırlatması gerektiğinde
                SMS veya WhatsApp ile tek tuşla müşterinize ulaşın.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Otomatik Aşı Hatırlatması', desc: 'Aşı tarihi yaklaşınca müşteriye otomatik SMS gider. Siz uğraşmazsınız.' },
                  { title: 'Borç Bildirimi', desc: 'Bakiye ekranından tek tuşla SMS veya WhatsApp ile borç hatırlatması gönderin.' },
                  { title: 'WhatsApp Desteği', desc: 'Mesaj otomatik hazırlanır, tek tıkla WhatsApp açılır. Numara ve metin hazır.' },
                  { title: 'Test & Şablon Yönetimi', desc: 'SMS şablonlarınızı düzenleyin. Göndermeden önce test numarasıyla deneyin.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-sky-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={14} className="text-sky-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{f.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="landing-reveal-right">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-sky-100/60 via-blue-50/40 to-transparent rounded-[2rem] blur-2xl" />
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-5 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900 text-sm">Bildirimler</span>
                    <span className="text-[10px] bg-sky-100 text-sky-600 px-2 py-0.5 rounded-full font-medium">3 bekleyen</span>
                  </div>
                  {[
                    { to: 'Ayşe K.', pet: 'Pamuk', msg: 'Karma Aşı hatırlatması', type: 'sms', color: 'border-l-blue-500 bg-blue-50' },
                    { to: 'Mehmet D.', pet: 'Max', msg: '750 TL borç bildirimi', type: 'whatsapp', color: 'border-l-green-500 bg-green-50' },
                    { to: 'Fatma Y.', pet: 'Boncuk', msg: 'Randevu onayı', type: 'sms', color: 'border-l-blue-500 bg-blue-50' },
                  ].map((item, i) => (
                    <div key={i} className={`rounded-xl border border-gray-100 border-l-[3px] ${item.color} px-4 py-3 flex items-center justify-between`}>
                      <div>
                        <p className="font-semibold text-gray-900 text-xs">{item.to} <span className="font-normal text-gray-400">({item.pet})</span></p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{item.msg}</p>
                      </div>
                      <span className={`text-[10px] font-medium px-2 py-1 rounded-full ${item.type === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {item.type === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── HOW IT WORKS ─────── */}
      <section id="how-it-works" className="py-24 px-4 bg-[#FAFAFA] relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#6b6de3]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="landing-reveal inline-flex items-center gap-2 bg-[#6b6de3]/10 text-[#6b6de3] px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Zap size={15} /> 3 Basit Adım
            </div>
            <h2 className="landing-reveal stagger-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight">Nasıl Çalışır?</h2>
            <p className="landing-reveal stagger-2 text-gray-500 mt-4 max-w-md mx-auto text-base">Kliniğinizi dijitale taşımak dakikalar alır</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 relative">
            {/* Connecting line - desktop only */}
            <div className="hidden md:block absolute top-[4.5rem] left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-[2px]">
              <div className="w-full h-full bg-gradient-to-r from-[#6b6de3]/30 via-[#6b6de3]/60 to-[#6b6de3]/30 rounded-full" />
            </div>

            {[
              { step: '1', title: 'Müşteriyi Bulun', desc: 'Telefon veya isimle arayın. Tek sonuç kaldığında Enter ile anında seçin. Yeni müşteri? Saniyeler içinde ekleyin.', icon: Users, gradient: 'from-[#6b6de3] to-[#8b5cf6]' },
              { step: '2', title: 'İşlemi Kaydedin', desc: 'Aşı, tedavi veya ürün satışı ekleyin. Ödemeyi alın, notu yazın, kiloyu girin. Her şey tek ekranda.', icon: Syringe, gradient: 'from-emerald-500 to-teal-400' },
              { step: '3', title: 'Gerisini Bize Bırakın', desc: 'Aşı hatırlatmaları, borç bildirimleri, stok uyarıları ve raporlar otomatik çalışsın. Siz işinize odaklanın.', icon: Bell, gradient: 'from-amber-500 to-orange-400' },
            ].map((s, i) => (
              <div key={i} className={`landing-reveal-scale stagger-${i + 3} relative`}>
                <div className="bg-white rounded-3xl p-7 border border-gray-100 shadow-lg shadow-gray-100/50 hover:shadow-xl hover:shadow-gray-200/60 transition-all duration-300 text-center group cursor-default h-full">
                  {/* Step number badge */}
                  <div className="relative mx-auto mb-6 w-[6rem] h-[6rem]">
                    <div className={`absolute inset-0 bg-gradient-to-br ${s.gradient} rounded-3xl opacity-10 group-hover:opacity-20 transition-opacity`} />
                    <div className={`w-full h-full bg-gradient-to-br ${s.gradient} rounded-3xl flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform duration-300`}>
                      <s.icon size={36} className="text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-gray-100">
                      <span className="text-sm font-extrabold text-[#6b6de3]">{s.step}</span>
                    </div>
                  </div>
                  <h3 className="font-extrabold text-gray-900 text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── INTEGRATIONS ─────── */}
      <section id="integrations" className="py-24 px-4 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-[#6b6de3]/5 to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="landing-reveal inline-flex items-center gap-2 bg-[#6b6de3]/10 text-[#6b6de3] px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Plug size={15} /> Entegrasyonlar
            </div>
            <h2 className="landing-reveal stagger-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight">Kullandığınız araçlarla <span className="text-[#6b6de3]">uyumlu</span></h2>
            <p className="landing-reveal stagger-2 text-gray-500 mt-4 max-w-lg mx-auto text-base">e-Fatura, SMS, ödeme ve daha fazlası. Tüm entegrasyonlar hazır, kuruluma gerek yok.</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-3xl mx-auto" data-testid="integrations-grid">
            {[
              { name: 'Paraşüt', desc: 'e-Fatura ve e-Arşiv fatura oluşturma', logo: PARASUT_LOGO, gradient: 'from-blue-50 to-sky-50', borderHover: 'hover:border-blue-200' },
              { name: 'NetGSM', desc: 'Otomatik ve manuel SMS gönderimi', logo: NETGSM_LOGO, gradient: 'from-red-50 to-rose-50', borderHover: 'hover:border-red-200' },
              { name: 'PayTR', desc: 'Güvenli online ödeme altyapısı', logo: PAYTR_LOGO, gradient: 'from-indigo-50 to-violet-50', borderHover: 'hover:border-indigo-200' },
              { name: 'WhatsApp', desc: 'Tek tıkla müşteriye mesaj gönderme', logo: WHATSAPP_LOGO, gradient: 'from-green-50 to-emerald-50', borderHover: 'hover:border-green-200' },
            ].map((int, i) => (
              <div key={i} className={`landing-reveal-scale stagger-${i + 3} group`} data-testid={`integration-${int.name.toLowerCase()}`}>
                <div className={`bg-gradient-to-br ${int.gradient} rounded-3xl p-6 border border-gray-100 ${int.borderHover} hover:shadow-xl transition-all duration-300 h-full text-center cursor-default`}>
                  <div className="w-20 h-20 bg-white rounded-2xl shadow-md border border-gray-100/50 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:shadow-lg transition-all duration-300 overflow-hidden">
                    {int.logo ? (
                      <img src={int.logo} alt={int.name} className="w-14 h-14 object-contain" />
                    ) : (
                      <Plug size={30} className="text-[#6b6de3]" />
                    )}
                  </div>
                  <h3 className="font-extrabold text-gray-900 text-sm mb-1.5">{int.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{int.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="landing-reveal stagger-8 mt-12 text-center">
            <p className="text-sm text-gray-400 font-medium">Tüm entegrasyonlar kurulum gerektirmez. Ayarlar panelinden aktif edin, hemen kullanmaya başlayın.</p>
          </div>
        </div>
      </section>

      {/* ─────── STATS / SOCIAL PROOF ─────── */}
      <section className="py-24 px-4 bg-[#6b6de3] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto relative">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-white text-center">
            {[
              { icon: TrendingUp, value: '%40', label: 'Daha az veresiye kaybı' },
              { icon: Clock, value: '2 saat', label: 'Günlük zaman tasarrufu' },
              { icon: Star, value: '4.9/5', label: 'Kullanıcı memnuniyeti' },
              { icon: Lock, value: '%100', label: 'Veri güvenliği' },
            ].map((s, i) => (
              <div key={i} className={`landing-reveal stagger-${i + 1} group`}>
                <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 group-hover:scale-110 transition-all duration-300">
                  <s.icon size={26} className="text-white/80" />
                </div>
                <p className="text-3xl sm:text-5xl font-extrabold mb-1">{s.value}</p>
                <p className="text-sm text-white/60 font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── SECURITY ─────── */}
      <section className="py-20 px-4 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="landing-reveal text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">Verileriniz güvende</h2>
          <p className="landing-reveal stagger-1 text-gray-500 mb-12 max-w-md mx-auto">Banka düzeyinde güvenlik, günlük yedekleme</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Lock, title: 'Şifreli Bağlantı', desc: 'Tüm veriler SSL/TLS ile şifrelenir.' },
              { icon: CloudUpload, title: 'Günlük Yedekleme', desc: 'Her gece otomatik yedekleme. Kayıp riski sıfır.' },
              { icon: Shield, title: 'Erişim Kontrolü', desc: 'Kimin ne göreceğine siz karar verin.' },
            ].map((s, i) => (
              <div key={i} className={`landing-reveal-scale stagger-${i + 2} bg-white rounded-2xl p-6 border border-gray-100`}>
                <div className="w-12 h-12 bg-[#6b6de3]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <s.icon size={24} className="text-[#6b6de3]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1.5">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── FAQ ─────── */}
      <section id="faq" className="py-20 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="landing-reveal text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-12 tracking-tight">Sıkça Sorulan Sorular</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`landing-reveal stagger-${i + 1} bg-[#FAFAFA] rounded-2xl border border-gray-100 overflow-hidden`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`faq-${i}`}>
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                  {openFaq === i ? <ChevronUp size={18} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── DEMO FORM ─────── */}
      <section id="demo-form" className="py-20 px-4 bg-[#FAFAFA]">
        <div className="max-w-md mx-auto">
          <div className="landing-reveal text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-3">Hemen Başvurun</h2>
            <p className="text-gray-500">Bilgilerinizi doldurun, onaylandığında hemen giriş yapın.</p>
          </div>
          {submitted ? (
            <div className="bg-green-50 rounded-2xl p-8 text-center border border-green-100">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Başvurunuz Alındı!</h3>
              <p className="text-gray-500 text-sm">Onaylandığında telefon numaranız ve şifrenizle giriş yapabileceksiniz.</p>
            </div>
          ) : (
            <form onSubmit={handleDemoSubmit} className="bg-white rounded-2xl p-6 space-y-4 shadow-lg shadow-gray-100/50 border border-gray-100">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Klinik Adı</label>
                <Input type="text" placeholder="Örn: Dostlar Veteriner Kliniği" value={demoForm.clinic_name}
                  onChange={(e) => setDemoForm({ ...demoForm, clinic_name: e.target.value })}
                  className="h-12 text-sm bg-[#FAFAFA] border-0 rounded-xl" required data-testid="demo-clinic-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">İl</label>
                <select value={demoForm.city} onChange={(e) => setDemoForm({ ...demoForm, city: e.target.value })}
                  className="w-full h-12 px-3 text-sm bg-[#FAFAFA] border-0 rounded-xl text-gray-900 appearance-none cursor-pointer" required data-testid="demo-city-select">
                  <option value="">İl seçin...</option>
                  {TURKEY_CITIES.map(city => <option key={city} value={city}>{city}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ad Soyad</label>
                  <Input type="text" placeholder="Adınız Soyadınız" value={demoForm.name}
                    onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                    className="h-12 text-sm bg-[#FAFAFA] border-0 rounded-xl" required data-testid="demo-name-input" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Telefon</label>
                  <Input type="tel" placeholder="05XXXXXXXXX" value={demoForm.phone}
                    onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                    inputMode="numeric" maxLength={11}
                    className="h-12 text-sm bg-[#FAFAFA] border-0 rounded-xl" required data-testid="demo-phone-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Şifre</label>
                <Input type="password" placeholder="En az 6 karakter" value={demoForm.password}
                  onChange={(e) => setDemoForm({ ...demoForm, password: e.target.value })}
                  className="h-12 text-sm bg-[#FAFAFA] border-0 rounded-xl" required data-testid="demo-password-input" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-13 py-3.5 text-sm font-bold bg-[#6b6de3] hover:bg-[#5658c4] rounded-full" data-testid="demo-submit-btn">
                {submitting ? 'Gönderiliyor...' : 'Ücretsiz Dene'}
              </Button>
              <p className="text-[11px] text-center text-gray-400">Başvurunuz onaylandığında telefon ve şifrenizle giriş yapabilirsiniz.</p>
            </form>
          )}
        </div>
      </section>

      {/* ─────── MOBILE APP ─────── */}
      <section className="py-20 px-4 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="landing-reveal-left order-2 md:order-1">
              <div className="relative max-w-xs mx-auto">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#6b6de3]/20 via-purple-100/30 to-transparent rounded-[2rem] blur-2xl" />
                <div className="relative bg-gray-900 rounded-[2rem] p-3 shadow-2xl">
                  <div className="bg-white rounded-[1.5rem] p-4 space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <img src={LOGO_URL} alt="AGROSVET" className="w-8 h-8" />
                      <span className="font-extrabold text-gray-900 text-sm">AGROSVET</span>
                    </div>
                    <div className="bg-[#6b6de3]/10 rounded-xl p-3">
                      <p className="text-xs font-semibold text-[#6b6de3] mb-1">Bugünkü Randevular</p>
                      <p className="text-2xl font-extrabold text-gray-900">8</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-green-50 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-base">🐕</span>
                          <div>
                            <p className="text-xs font-bold text-gray-900">Max - Kuduz</p>
                            <p className="text-[10px] text-gray-400">09:00 - Ahmet Y.</p>
                          </div>
                        </div>
                        <Check size={14} className="text-green-600" />
                      </div>
                      <div className="flex items-center justify-between bg-blue-50 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-base">🐈</span>
                          <div>
                            <p className="text-xs font-bold text-gray-900">Luna - Karma</p>
                            <p className="text-[10px] text-gray-400">10:30 - Ayşe K.</p>
                          </div>
                        </div>
                        <Phone size={14} className="text-blue-600" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="landing-reveal-right order-1 md:order-2">
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Smartphone size={15} /> Mobil Uygulama
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
                Kliniğiniz cebinizde
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                AGROSVET'i telefonunuza yükleyin, her yerden kliniğinizi yönetin.
                PWA teknolojisi ile uygulama mağazasına gerek kalmadan kurulum yapın.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Tam mobil uyumlu tasarım',
                  'Çevrimdışı erişim desteği',
                  'Anlık bildirimler',
                  'Tek dokunuşla arama ve SMS',
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check size={16} className="text-emerald-600 shrink-0" />
                    <span className="text-sm text-gray-700">{t}</span>
                  </div>
                ))}
              </div>

              {/* App Store Badges */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Yakında Mağazalarda</p>
                <div className="flex gap-3">
                  <div className="flex items-center gap-2.5 bg-gray-900 text-white pl-3 pr-5 py-2.5 rounded-xl cursor-pointer hover:bg-gray-800 transition-colors" data-testid="app-store-badge">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    <div>
                      <p className="text-[9px] text-white/60 leading-none">Yakında</p>
                      <p className="text-sm font-semibold leading-tight">App Store</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 bg-gray-900 text-white pl-3 pr-5 py-2.5 rounded-xl cursor-pointer hover:bg-gray-800 transition-colors" data-testid="google-play-badge">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92z" fill="#4285F4"/><path d="M17.194 8.598l-3.402 3.402 3.402 3.402 3.836-2.17a1 1 0 000-1.732l-3.836-2.902z" fill="#FBBC04"/><path d="M3.609 22.186L13.792 12l3.402 3.402-10.183 5.916a1.019 1.019 0 01-3.402.868z" fill="#EA4335"/><path d="M3.609 1.814a1.019 1.019 0 013.402.868L17.194 8.598 13.792 12 3.609 1.814z" fill="#34A853"/></svg>
                    <div>
                      <p className="text-[9px] text-white/60 leading-none">Yakında</p>
                      <p className="text-sm font-semibold leading-tight">Google Play</p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400">Şimdilik telefonunuzun tarayıcısından "Ana Ekrana Ekle" ile kurabilirsiniz.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── TESTIMONIALS ─────── */}
      <section className="py-24 px-4 bg-white overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="landing-reveal inline-flex items-center gap-2 bg-amber-50 text-amber-600 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Star size={15} /> Kullanıcı Yorumları
            </div>
            <h2 className="landing-reveal stagger-1 text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              Veteriner hekimler ne diyor?
            </h2>
          </div>
        </div>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          <div className="testimonial-track" data-testid="testimonials">
            {[
              { name: 'Dr. Emre K.', clinic: 'Pet Şifa Veteriner', text: 'Klinik yönetimimiz tamamen değişti. Stok takibinden veresiye yönetimine kadar her şey çok kolay.' },
              { name: 'Dr. Selin A.', clinic: 'Dostlar Veteriner', text: 'Müşterilerimiz artık kendi randevusunu alıyor. Telefon trafiğimiz yarısına düştü.' },
              { name: 'Dr. Can R.', clinic: 'Patiler Veteriner', text: 'SMS hatırlatma sistemi harika. Aşı kaçıranlar neredeyse sıfıra indi.' },
              { name: 'Dr. Zeynep T.', clinic: 'Sevgi Veteriner', text: 'Hasta dosyası özelliği muhteşem. Lab sonuçları, kilo takibi hepsi bir arada.' },
              { name: 'Dr. Mert B.', clinic: 'Hayat Veteriner', text: 'Mobil uyumlu olması çok büyük avantaj. Klinikte olmasam bile her şeyi takip edebiliyorum.' },
              { name: 'Dr. Ayşe D.', clinic: 'Pati Dostları', text: 'Ekip yönetimi ve yetkilendirme sistemi tam ihtiyacımız olanı karşıladı. Herkes kendi işini görüyor.' },
              { name: 'Dr. Emre K.', clinic: 'Pet Şifa Veteriner', text: 'Klinik yönetimimiz tamamen değişti. Stok takibinden veresiye yönetimine kadar her şey çok kolay.' },
              { name: 'Dr. Selin A.', clinic: 'Dostlar Veteriner', text: 'Müşterilerimiz artık kendi randevusunu alıyor. Telefon trafiğimiz yarısına düştü.' },
              { name: 'Dr. Can R.', clinic: 'Patiler Veteriner', text: 'SMS hatırlatma sistemi harika. Aşı kaçıranlar neredeyse sıfıra indi.' },
              { name: 'Dr. Zeynep T.', clinic: 'Sevgi Veteriner', text: 'Hasta dosyası özelliği muhteşem. Lab sonuçları, kilo takibi hepsi bir arada.' },
              { name: 'Dr. Mert B.', clinic: 'Hayat Veteriner', text: 'Mobil uyumlu olması çok büyük avantaj. Klinikte olmasam bile her şeyi takip edebiliyorum.' },
              { name: 'Dr. Ayşe D.', clinic: 'Pati Dostları', text: 'Ekip yönetimi ve yetkilendirme sistemi tam ihtiyacımız olanı karşıladı. Herkes kendi işini görüyor.' },
            ].map((t, i) => (
              <div key={i} className="flex-shrink-0 w-80 mx-3">
                <div className="bg-[#FAFAFA] rounded-2xl p-6 border border-gray-100 h-full">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} className="text-amber-400 fill-amber-400" />)}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#6b6de3] to-[#a78bfa] rounded-full flex items-center justify-center">
                      <User size={18} className="text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.clinic}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── CTA BANNER ─────── */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#6b6de3] via-[#7c7ef0] to-[#a78bfa]" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-20 h-20 bg-white/5 rounded-full float-slow" />
          <div className="absolute bottom-10 right-10 w-16 h-16 bg-white/5 rounded-2xl float-slower" />
          <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-white/5 rounded-full float-slower" style={{animationDelay:'2s'}} />
        </div>
        <div className="max-w-3xl mx-auto text-center text-white relative">
          <h2 className="landing-reveal text-3xl sm:text-4xl font-extrabold mb-4">Kliniğinizi modernleştirmeye hazır mısınız?</h2>
          <p className="landing-reveal stagger-1 text-white/70 mb-10 text-lg">15 gün ücretsiz deneyin. Kredi kartı gerekmez.</p>
          <div className="landing-reveal stagger-2">
          <Button onClick={() => scrollTo('demo-form')} className="bg-white text-[#6b6de3] hover:bg-gray-100 rounded-full text-base px-10 h-14 py-3.5 font-bold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300">
            Hemen Başlayın <ArrowRight size={18} className="ml-2" />
          </Button>
          </div>
        </div>
      </section>

      {/* ─────── FOOTER ─────── */}
      <footer className="py-12 px-4 bg-gray-900">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <img src={LOGO_LIGHT} alt="AGROSVET" className="w-8 h-8 object-contain" />
              <span className="font-extrabold text-[#c8caff] text-lg tracking-tight">AGROSVET</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              {navLinks.map(l => (
                <button key={l.id} onClick={() => scrollTo(l.id)} className="hover:text-white transition-colors">{l.label}</button>
              ))}
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-1.5" data-testid="footer-address">
                <MapPin size={14} className="text-gray-500 flex-shrink-0" />
                <span>Bahçelievler mh. 3034 sk. No:5 Merkez / Isparta</span>
              </div>
              <a href="tel:05527370032" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone size={14} className="text-gray-500 flex-shrink-0" />
                <span>0552 737 00 32</span>
              </a>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-6 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} AgrosTech. Isparta'da sevgiyle yapıldı.</p>
            <div className="flex items-center gap-4 text-sm">
              <a href="/gizlilik" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-privacy-link">Gizlilik Politikası</a>
              <span className="text-gray-700">|</span>
              <a href="/kvkk" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-kvkk-link">KVKK Aydınlatma Metni</a>
              <span className="text-gray-700">|</span>
              <a href="mailto:info@agrostech.net" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-email-link">info@agrostech.net</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
